<template>
  <div v-if="functionData && hasContent" :style="style" class="vehicle-selector-function-info">
    <!-- TOGGLE -->
    <h2
      v-if="functionData.heading"
      class="sr-only"
      v-html="functionData.heading"></h2>
    <button
      class="vehicle-selector-function-info__toggle"
      type="button"
      :aria-controls="`${id}-content`"
      :aria-expanded="isOpen"
      @click="toggleOpen">
      <oie-base-icon size="l" icon="information"></oie-base-icon>
      <span class="h3-bold" v-html="functionData.heading || defaultHeading"></span>
      <div>
        <span>{{ isOpen ? tStatic('genericClose') : tStatic('genericOpen') }}</span>
        <oie-base-icon size="l" :icon="isOpen ? 'chevron-up' : 'chevron-down'"></oie-base-icon>
      </div>
    </button>

    <!-- CONTENT -->
    <div v-if="isOpen" :id="`${id}-content`" class="vehicle-selector-function-info__content">
      <div class="vehicle-selector-function-info__layout">
        <!-- MEDIA -->
        <div v-if="functionData.mediaAssetsUrl && functionData.mediaAssetsUrl.length > 0" class="vehicle-selector-function-info__media">
          <div class="vehicle-selector-function-info__media-container">
            <div v-if="functionData.mediaAssetsUrl.length > 1" class="vehicle-selector-function-info__thumbs">
              <button
                v-for="(url, index) in functionData.mediaAssetsUrl"
                :key="index"
                :class="['vehicle-selector-function-info__thumb', { 'vehicle-selector-function-info__thumb--active': selectedImageIndex === index }]"
                @click="selectImage(index)">
                <img
                  :src="`${url}?preset=ProductSmall&watermark=OIEWatermarkSmall`"
                  :alt="`${functionData.heading || defaultHeading} - thumbnail ${index + 1}`"
                  class="vehicle-selector-function-info__thumb-image">
              </button>
            </div>
            <div
              v-show="selectedImageUrl && (isMobile || !functionData.mediaAssetsData || !functionData.mediaAssetsData.some(item => item.url.includes(functionData.mediaAssetsUrl[selectedImageIndex])))"
              class="vehicle-selector-function-info__main-image"
              @click="openImageInspector">
              <oie-base-action
                v-if="functionData.mediaAssetsUrl.length > 1 && selectedImageIndex > 0"
                :aria-label="tStatic('previous')"
                theme="outlined-black"
                round
                class="vehicle-selector-function-info__nav-button vehicle-selector-function-info__nav-button--prev"
                @click.stop="selectImage(selectedImageIndex - 1)">
                <oie-base-icon icon="chevron-left"></oie-base-icon>
              </oie-base-action>
              <oie-base-action
                v-if="functionData.mediaAssetsUrl.length > 1 && selectedImageIndex < functionData.mediaAssetsUrl.length - 1"
                :aria-label="tStatic('next')"
                theme="outlined-black"
                round
                class="vehicle-selector-function-info__nav-button vehicle-selector-function-info__nav-button--next"
                @click.stop="selectImage(selectedImageIndex + 1)">
                <oie-base-icon icon="chevron-right"></oie-base-icon>
              </oie-base-action>
              <img
                :src="selectedImageUrl"
                :alt="functionData.heading || defaultHeading"
                class="vehicle-selector-function-info__image">
              <button class="vehicle-selector-function-info__zoom-icon" aria-label="Zoom image">
                <oie-use-svg svg="zoom"></oie-use-svg>
              </button>
            </div>
            <vehicle-selector-function-info-interactive-map
              v-if="!isMobile"
              v-show="functionData.mediaAssetsData && functionData.mediaAssetsData.length > 0 && functionData.mediaAssetsData.some(item => item.url.includes(functionData.mediaAssetsUrl[selectedImageIndex]))"
              :media-assets-data="functionData.mediaAssetsData"
              :media-assets-url="functionData.mediaAssetsUrl"
              :selected-image-index="selectedImageIndex"
              :alt-text="functionData.heading || defaultHeading"
              :id-prefix="id"
              :products="products"
              @select-image="selectImage"
              @open-inspector="openImageInspector">
            </vehicle-selector-function-info-interactive-map>
          </div>
        </div>

        <!-- TEXT -->
        <div class="vehicle-selector-function-info__text">
          <div>
            <p
              v-if="functionData.heading"
              class="body-bold"
              v-html="functionData.heading"></p>

            <p
              v-if="functionData.headingDescription"
              v-html="functionData.headingDescription"></p>
          </div>

          <oie-text-formatter
            v-if="functionData.description"
            :content="functionData.description"></oie-text-formatter>

          <oie-technical-facts v-if="functionData.specification" :technical-data="parseSpec(functionData.specification)"></oie-technical-facts>
          <!--<oie-text-formatter
            v-if="functionData.specification"
            :content="functionData.specification"></oie-text-formatter>-->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted, onUnmounted } from '@vue/composition-api'
import _ from 'lodash'
import VehicleSelectorFunctionInfoInteractiveMap from './vehicle-selector-function-info-interactive-map.vue'
import httpService from '@/core/http-service'
import priceService from '@/core/price-service'

const STORAGE_KEY = 'vehicle-selector-function-info-state'

export default defineComponent({
  components: { VehicleSelectorFunctionInfoInteractiveMap },
  props: {
    functionData: {
      type: Object,
      default: null
    },
    marginBelow: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { root }) {
    const defaultHeading = 'Identifiera din modell'
    const selectedImageIndex = ref(0)
    const id = _.uniqueId('vehicle-selector-function-info-')
    const products = ref([])

    const hasContent = computed(() => {
      if (!props.functionData) return false
      return !!(
        props.functionData.heading ||
        props.functionData.headingDescription ||
        props.functionData.description ||
        props.functionData.specification ||
        (props.functionData.mediaAssetsUrl && props.functionData.mediaAssetsUrl.length > 0)
      )
    })

    const selectedImageUrl = computed(() => {
      if (!props.functionData || !props.functionData.mediaAssetsUrl || !props.functionData.mediaAssetsUrl.length) {
        return null
      }
      return props.functionData.mediaAssetsUrl[selectedImageIndex.value]
    })

    const style = computed(() => {
      return props.marginBelow ? 'margin-bottom: 3rem;' : ''
    })

    const parseSpec = (specString) => {
      if (typeof specString === 'object') return specString
      try {
        return JSON.parse(specString)
      } catch {
        return specString
      }
    }

    // Get stored state from localStorage
    const getStoredState = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored !== null) {
          return JSON.parse(stored)
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e)
      }
      return true // Default to open
    }

    const isOpen = ref(getStoredState())

    const mediaQuery = window.matchMedia('(max-width: 650px)')
    const isMobile = ref(mediaQuery.matches)
    const onMediaQueryChange = (e) => { isMobile.value = e.matches }

    const toggleOpen = () => {
      isOpen.value = !isOpen.value
      saveState(isOpen.value)
    }

    const closeInfo = () => {
      isOpen.value = false
      saveState(false)
    }

    const saveState = (state) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
      } catch (e) {
        console.error('Error writing to localStorage:', e)
      }
    }

    // Watch for changes in function data and reset to stored state
    watch(() => props.functionData, () => {
      isOpen.value = getStoredState()
      selectedImageIndex.value = 0
    })

    const selectImage = (index) => {
      selectedImageIndex.value = index
    }

    const openImageInspector = () => {
      const images = props.functionData.mediaAssetsUrl.map((url, index) => ({
        url,
        imageId: index
      }))
      const mediaAssetsData = props.functionData.mediaAssetsData || []
      // Merge mediaAssetsData into images based on URL matching
      images.forEach(image => {
        const matchingData = mediaAssetsData.find(data => data.url.includes(image.url))
        if (matchingData) {
          image.data = matchingData
        }
      })
      root.publish('modal.show.image-inspector', {
        images,
        displayIndex: selectedImageIndex.value,
        products: products.value
      })
    }

    const getProducts = async (codes) => {
      if (!codes.length) {
        return
      }

      const response = await httpService.post('completeProducts', { page: 1, pageSize: codes.length, productIds: codes })
      const fetchedProducts = response.data.data
      const productIds = _.flatMap(response.data.data, 'id')
      await setProductListPrices(productIds, fetchedProducts)
      products.value = fetchedProducts
    }

    const setProductListPrices = async (ids, products) => {
      const priceInformation = await httpService.post('productListPrices', { productId: ids })
      priceService.populatePricesFromBackend(priceInformation.data, products)
    }

    onMounted(() => {
      mediaQuery.addEventListener('change', onMediaQueryChange)
    })

    onUnmounted(() => {
      mediaQuery.removeEventListener('change', onMediaQueryChange)
    })

    onMounted(() => {
      const mediaAssetsData = (props.functionData && props.functionData.mediaAssetsData) || []
      const codes = mediaAssetsData
        .flatMap(item => (item.merchendiseImageMap && item.merchendiseImageMap.areas) || [])
        .filter(area => area.entityType !== 'ChannelNode')
        .map(area => area.code)
        .filter(Boolean)
      getProducts(codes)
    })

    return {
      id,
      defaultHeading,
      hasContent,
      isOpen,
      toggleOpen,
      closeInfo,
      selectedImageIndex,
      selectedImageUrl,
      selectImage,
      openImageInspector,
      parseSpec,
      style,
      getProducts,
      setProductListPrices,
      products,
      isMobile
    }
  }
})
</script>

<style lang="scss">
.vehicle-selector-function-info {
  background-color: color(background-blue-4);
  border: 1px solid color(border-blue-3);
  border-radius: 4px;
  width: 100%;
}

// TOGGLE
.vehicle-selector-function-info__toggle {
  @include reset-button;
  width: 100%;
  display: flex;
  align-items: center;
  gap: measure(quarter);
  padding: measure(half) measure(full);
  background-color: transparent;
}

.vehicle-selector-function-info__toggle >:last-child {
  display: flex;
  align-items: center;
  gap: measure(quarter);
  margin-left: auto;
}

.vehicle-selector-function-info__content {
  padding: measure(full);
}

.vehicle-selector-function-info__layout {
  display: grid;
  gap: measure(full);

  @include media(medium) {
    grid-template-columns: 1fr 1fr;
    gap: measure(double);
  }
}

.vehicle-selector-function-info__media {
  display: flex;
  flex-direction: column;
  gap: measure(half);
}

.vehicle-selector-function-info__media-container {
  display: flex;
  gap: measure(half);
  flex-direction: column;

  @include media(small) {
    flex-direction: row;
  }
}

.vehicle-selector-function-info__thumbs {
  display: flex;
  gap: measure(quarter);
  flex-direction: row;
  flex-wrap: wrap;

  @include media(small) {
    flex-direction: column;
    order: -1;
  }
}

.vehicle-selector-function-info__thumb {
  @include reset-button;
  position: relative;
  width: 70px;
  height: 70px;
  border: 2px solid color(border-blue-3);
  border-radius: 4px;
  overflow: hidden;
  background-color: color(background-white);
  cursor: pointer;
  transition: border-color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    border-color: color(border-blue-1);
  }
}

.vehicle-selector-function-info__thumb--active {
  border-color: color(border-blue-1);
  border-width: 2px;
}

.vehicle-selector-function-info__thumb-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 4px;
}

.vehicle-selector-function-info__main-image {
  position: relative;
  width: 100%;
  padding-bottom: 75%; // 4:3 aspect ratio
  background-color: color(background-white);
  border: 1px solid color(border-blue-3);
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease;
  flex: 1;

  &:hover {
    border-color: color(border-blue-1);
  }

  &:hover .vehicle-selector-function-info__zoom-icon,
  &:hover .vehicle-selector-function-info__nav-button {
    opacity: 1;
  }
}

.vehicle-selector-function-info__nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;

  &:focus {
    outline: 1px solid color(black);
    outline-offset: 2px;
    opacity: 1;
  }
  &:focus:not(:focus-visible) { outline: none; }

  &:hover {
    opacity: 1;
  }
}

.vehicle-selector-function-info__nav-button--prev {
  left: measure(quarter);
}

.vehicle-selector-function-info__nav-button--next {
  right: measure(quarter);
}

.vehicle-selector-function-info__image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 1.25rem 2.5rem;
}

.vehicle-selector-function-info__zoom-icon {
  @include reset-button;
  position: absolute;
  top: measure(quarter);
  right: measure(quarter);
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: color(background-white-semi);
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: color(border-blue-1);
  }
}

.vehicle-selector-function-info__text {
  display: flex;
  flex-direction: column;
  gap: measure(half);
}

</style>
